import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { BookOpen, Search, X, Plus, List, ThumbsUp, Meh, ThumbsDown, ChevronLeft, ChevronRight, Loader2, Trophy, Info, Calendar as CalendarIcon, ArrowUp, ArrowDown, Check, Clock, ListOrdered, GripVertical, Share2, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';
import { AddToListDialog } from '../components/AddToListDialog';
import { format } from 'date-fns';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, snapCenterToCursor } from '@dnd-kit/modifiers';
import { searchMedia, discoverMedia, getGenres, toGoogleBooksFormat, getBookDetails, MediaType } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BooksDiscoverPage({ user }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fictionFilter, setFictionFilter] = useState(null); // "fiction", "nonfiction", or null
  const [orderBy, setOrderBy] = useState('relevance');
  
  // Book lists for adding
  const [bookLists, setBookLists] = useState([]);
  const [readBooks, setReadBooks] = useState([]);
  
  // Details dialog
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookDetails, setBookDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Mark as read dialog
  const [markingBook, setMarkingBook] = useState(null);
  const [grade, setGrade] = useState('liked');
  const [comment, setComment] = useState('');
  const [isFirstRead, setIsFirstRead] = useState(true);
  const [readDate, setReadDate] = useState(null);
  const [primaryGenre, setPrimaryGenre] = useState(null);
  const [customGenre, setCustomGenre] = useState('');
  
  // Ranking dialog
  const [showRanking, setShowRanking] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Add to list dialog state
  const [addToListItem, setAddToListItem] = useState(null);
  const [addingToList, setAddingToList] = useState(false);
  
  // Add & Rank state for lists
  const [rankingListBook, setRankingListBook] = useState(null);
  const [rankingList, setRankingList] = useState(null);
  const [listRankingItems, setListRankingItems] = useState([]);
  const [activeRankId, setActiveRankId] = useState(null);
  
  // Share with friend state
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [sharingWith, setSharingWith] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchCategories();
    fetchUserData();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      searchBooks();
    } else {
      discoverBooks();
    }
  }, [page, selectedCategory, fictionFilter, orderBy]);

  const fetchCategories = async () => {
    try {
      // Use canonical media API for genres
      const genres = await getGenres(MediaType.BOOK);
      // Extract just the names since the page expects strings
      setCategories((genres || []).map(g => typeof g === 'object' ? g.name : g));
    } catch (error) {
      console.error('Failed to fetch categories');
      // Fallback to old API
      try {
        const res = await axios.get(`${API}/books/genres`, { withCredentials: true });
        setCategories(res.data.genres || []);
      } catch (e) {
        console.error('Fallback also failed');
      }
    }
  };

  const fetchUserData = async () => {
    try {
      const [listsRes, readRes] = await Promise.all([
        axios.get(`${API}/book-lists`, { withCredentials: true }),
        axios.get(`${API}/read-books`, { withCredentials: true })
      ]);
      setBookLists(listsRes.data || []);
      setReadBooks(readRes.data || []);
    } catch (error) {
      console.error('Failed to fetch user data');
    }
  };
  
  // Fetch friends for sharing
  const fetchFriends = async () => {
    setLoadingFriends(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/messages/friends`, { withCredentials: true, headers });
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setLoadingFriends(false);
    }
  };
  
  // Handle sharing book with a friend
  const handleShareWithFriend = async (friendId, book) => {
    setSharingWith(friendId);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/messages`, {
        recipient_id: friendId,
        content: shareMessage || `Check out ${book.title}!`,
        message_type: 'media_share',
        media_data: {
          media_type: 'book',
          item_id: book.book_id,
          title: book.title,
          poster: book.cover_image,
          year: book.published_date ? new Date(book.published_date).getFullYear() : null
        }
      }, { withCredentials: true, headers });
      
      toast.success('Shared with friend!');
      setShowSharePanel(false);
      setShareMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setSharingWith(null);
    }
  };
  
  // Open share panel
  const openSharePanel = () => {
    setShowSharePanel(true);
    fetchFriends();
  };

  const discoverBooks = async () => {
    setLoading(true);
    try {
      // Use canonical media API for discovery
      const result = await discoverMedia(MediaType.BOOK, {
        page,
        limit: 24,
        genre: selectedCategory,
        fictionFilter: fictionFilter,
        orderBy: orderBy
      });
      
      // Convert canonical items to Google Books format for compatibility
      const books = result.items.map(item => {
        const gbFormat = toGoogleBooksFormat(item);
        return {
          book_id: gbFormat.google_books_id || item.id,
          title: gbFormat.title,
          subtitle: gbFormat.subtitle,
          author: gbFormat.authors?.[0] || 'Unknown Author',
          authors: gbFormat.authors,
          description: gbFormat.description,
          cover_image: gbFormat.cover_image,
          thumbnail: gbFormat.thumbnail,
          page_count: gbFormat.page_count,
          published_date: gbFormat.published_date,
          publisher: gbFormat.publisher,
          genres: gbFormat.genres,
          categories: gbFormat.categories,
          average_rating: gbFormat.average_rating,
          ratings_count: gbFormat.ratings_count,
          series: gbFormat.series,
          _canonical: item
        };
      });
      
      setBooks(books);
      setTotalPages(result.totalPages || 1);
    } catch (error) {
      console.error('Canonical API failed, falling back:', error);
      // Fallback to old API
      try {
        const params = new URLSearchParams({ page: page.toString(), order_by: orderBy });
        if (selectedCategory) params.append('category', selectedCategory);
        if (fictionFilter) params.append('fiction_filter', fictionFilter);
        
        const res = await axios.get(`${API}/books/discover?${params}`, { withCredentials: true });
        setBooks(res.data.results || []);
        setTotalPages(res.data.total_pages || 1);
      } catch (e) {
        toast.error('Failed to load books');
      }
    } finally {
      setLoading(false);
    }
  };

  const searchBooks = async () => {
    if (!searchQuery.trim()) {
      discoverBooks();
      return;
    }
    
    setLoading(true);
    try {
      // Use canonical media API for search
      const result = await searchMedia(searchQuery, MediaType.BOOK, page, 24);
      
      // Convert canonical items to Google Books format for compatibility
      const books = result.items.map(item => {
        const gbFormat = toGoogleBooksFormat(item);
        return {
          book_id: gbFormat.google_books_id || item.id,
          title: gbFormat.title,
          subtitle: gbFormat.subtitle,
          author: gbFormat.authors?.[0] || 'Unknown Author',
          authors: gbFormat.authors,
          description: gbFormat.description,
          cover_image: gbFormat.cover_image,
          thumbnail: gbFormat.thumbnail,
          page_count: gbFormat.page_count,
          published_date: gbFormat.published_date,
          publisher: gbFormat.publisher,
          genres: gbFormat.genres,
          categories: gbFormat.categories,
          average_rating: gbFormat.average_rating,
          ratings_count: gbFormat.ratings_count,
          series: gbFormat.series,
          _canonical: item
        };
      });
      
      setBooks(books);
      setTotalPages(result.totalPages || 1);
    } catch (error) {
      console.error('Canonical search failed, falling back:', error);
      // Fallback to old API
      try {
        const res = await axios.get(`${API}/books/search?query=${encodeURIComponent(searchQuery)}&page=${page}`, { withCredentials: true });
        setBooks(res.data.results || []);
        setTotalPages(res.data.total_pages || 1);
      } catch (e) {
        toast.error('Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    searchBooks();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category === selectedCategory ? null : category);
    setPage(1);
    setSearchQuery('');
  };

  const fetchBookDetails = async (bookId) => {
    setLoadingDetails(true);
    try {
      // Use canonical media API for book details
      const details = await getBookDetails(bookId);
      setBookDetails(details);
    } catch (error) {
      console.error('Canonical book details failed, falling back:', error);
      // Fallback to old API
      try {
        const res = await axios.get(`${API}/books/${bookId}`, { withCredentials: true });
        setBookDetails(res.data);
      } catch (e) {
        toast.error('Failed to load book details');
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleBookClick = (book) => {
    setSelectedBook(book);
    fetchBookDetails(book.book_id);
  };

  const isBookRead = (bookId) => readBooks.some(b => b.book_id === bookId);
  const isBookInList = (bookId, listId) => {
    const list = bookLists.find(l => l.id === listId);
    return list?.books?.some(b => b.book_id === bookId);
  };

  // Handler for AddToListDialog
  const handleAddToListFromDialog = async (listId, book, position) => {
    setAddingToList(true);
    try {
      await axios.post(`${API}/book-lists/${listId}/books`, {
        book_id: book.book_id,
        title: book.title,
        author: book.author,
        cover_image: book.cover_image,
        description: book.description,
        page_count: book.page_count,
        genres: book.genres,
        position
      }, { withCredentials: true });
      
      toast.success(`Added "${book.title}" to list!`);
      setAddToListItem(null);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    } finally {
      setAddingToList(false);
    }
  };

  // Handler for Add & Rank functionality
  const handleAddAndRankFromDialog = async (list, book) => {
    setAddToListItem(null);
    setRankingListBook(book);
    setRankingList(list);
    
    // Get existing books in the list
    const existingBooks = list.books || [];
    setListRankingItems([
      ...existingBooks,
      {
        book_id: book.book_id,
        title: book.title,
        author: book.author,
        cover_image: book.cover_image,
        description: book.description,
        page_count: book.page_count,
        genres: book.genres,
        rank: existingBooks.length + 1,
        isNew: true
      }
    ]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveRankId(null);
    
    if (active.id !== over?.id) {
      setListRankingItems((items) => {
        const oldIndex = items.findIndex(i => i.book_id === active.id);
        const newIndex = items.findIndex(i => i.book_id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveListRanking = async () => {
    if (!rankingList || !rankingListBook) return;
    setSaving(true);
    
    try {
      // First add the book
      await axios.post(`${API}/book-lists/${rankingList.id}/books`, {
        book_id: rankingListBook.book_id,
        title: rankingListBook.title,
        author: rankingListBook.author,
        cover_image: rankingListBook.cover_image,
        description: rankingListBook.description,
        page_count: rankingListBook.page_count,
        genres: rankingListBook.genres,
        position: 'bottom'
      }, { withCredentials: true });
      
      // Then reorder
      const newOrder = listRankingItems.map(b => b.book_id);
      await axios.put(`${API}/book-lists/${rankingList.id}/reorder`, {
        book_order: newOrder
      }, { withCredentials: true });
      
      toast.success(`Added "${rankingListBook.title}" and ranked!`);
      setRankingListBook(null);
      setRankingList(null);
      setListRankingItems([]);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add and rank');
    } finally {
      setSaving(false);
    }
  };

  const handleAddToList = async (book, listId, position = 'bottom') => {
    try {
      await axios.post(`${API}/book-lists/${listId}/books`, {
        book_id: book.book_id,
        title: book.title,
        author: book.author,
        cover_image: book.cover_image,
        description: book.description,
        page_count: book.page_count,
        genres: book.genres,
        position
      }, { withCredentials: true });
      
      toast.success(`Added to list!`);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    }
  };

  const openMarkAsRead = (book) => {
    setMarkingBook(book);
    setGrade('liked');
    setComment('');
    setIsFirstRead(true);
    setReadDate(null);
    setPrimaryGenre(book.genres?.[0] || null);
  };

  const handleContinueToRanking = async () => {
    // Get existing items with same grade
    const sameGrade = readBooks.filter(b => b.grade === grade);
    setExistingItems(sameGrade.sort((a, b) => a.rank_in_group - b.rank_in_group));
    
    if (sameGrade.length === 0) {
      // No existing items, just save at rank 1
      await saveReadBook(1);
    } else {
      // Start binary search comparison
      setSearchLow(0);
      setSearchHigh(sameGrade.length);
      const midIndex = Math.floor(sameGrade.length / 2);
      setComparisonItem(sameGrade[midIndex]);
      setShowRanking(true);
    }
  };

  const handleComparisonChoice = async (preferNew) => {
    let newLow = searchLow;
    let newHigh = searchHigh;
    
    const currentIndex = existingItems.findIndex(i => i.id === comparisonItem.id);
    
    if (preferNew) {
      newHigh = currentIndex;
    } else {
      newLow = currentIndex + 1;
    }
    
    setSearchLow(newLow);
    setSearchHigh(newHigh);
    
    if (newLow >= newHigh) {
      await saveReadBook(newLow + 1);
    } else {
      const midIndex = Math.floor((newLow + newHigh) / 2);
      setComparisonItem(existingItems[midIndex]);
    }
  };

  const saveReadBook = async (rankInGroup) => {
    setSaving(true);
    try {
      await axios.post(`${API}/read-books/direct`, {
        book_id: markingBook.book_id,
        title: markingBook.title,
        author: markingBook.author,
        cover_image: markingBook.cover_image,
        description: markingBook.description,
        page_count: markingBook.page_count,
        genres: markingBook.genres,
        primary_genre: customGenre || primaryGenre,
        grade,
        rank_in_group: rankInGroup,
        comment: comment || null,
        is_first_read: isFirstRead,
        read_date: readDate ? format(readDate, 'yyyy-MM-dd') : null
      }, { withCredentials: true });
      
      toast.success(`Marked as read! Ranked #${rankInGroup} in ${grade}`);
      resetMarkDialog();
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as read');
    } finally {
      setSaving(false);
    }
  };

  const resetMarkDialog = () => {
    setMarkingBook(null);
    setShowRanking(false);
    setExistingItems([]);
    setComparisonItem(null);
    setSearchLow(0);
    setSearchHigh(0);
    setGrade('liked');
    setComment('');
    setIsFirstRead(true);
    setReadDate(null);
    setPrimaryGenre(null);
    setCustomGenre('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <AppHeader user={user} showBack={true} backPath="/books" />
      
      <main className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3c405b]">Discover Books</h1>
            <p className="text-[#64748B]">Find your next great read</p>
          </div>
        </div>
        
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, author..."
                className="pl-10 bg-white border-0 shadow-md"
                data-testid="book-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); setPage(1); discoverBooks(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
            <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="book-search-btn">
              Search
            </Button>
          </div>
        </form>
        
        {/* Fiction/Non-Fiction Filter */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={fictionFilter === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFictionFilter(null); setPage(1); }}
            className={`rounded-full ${fictionFilter === null ? 'bg-amber-500 hover:bg-amber-600' : 'hover:bg-amber-50'}`}
            data-testid="filter-all"
          >
            All Books
          </Button>
          <Button
            variant={fictionFilter === 'fiction' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFictionFilter('fiction'); setPage(1); }}
            className={`rounded-full ${fictionFilter === 'fiction' ? 'bg-amber-500 hover:bg-amber-600' : 'hover:bg-amber-50'}`}
            data-testid="filter-fiction"
          >
            Fiction
          </Button>
          <Button
            variant={fictionFilter === 'nonfiction' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFictionFilter('nonfiction'); setPage(1); }}
            className={`rounded-full ${fictionFilter === 'nonfiction' ? 'bg-amber-500 hover:bg-amber-600' : 'hover:bg-amber-50'}`}
            data-testid="filter-nonfiction"
          >
            Non-Fiction
          </Button>
        </div>
        
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.slice(0, 12).map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleCategoryChange(category)}
              className={`rounded-full ${selectedCategory === category ? 'bg-amber-500 hover:bg-amber-600' : 'hover:bg-amber-50'}`}
            >
              {category}
            </Button>
          ))}
          {categories.length > 12 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full">
                  More...
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-64 overflow-auto">
                {categories.slice(12).map((category) => (
                  <DropdownMenuItem key={category} onClick={() => handleCategoryChange(category)}>
                    {category}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Results Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>No books found. Try a different search or category.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">
              {books.map((book) => {
                const alreadyRead = isBookRead(book.book_id);
                return (
                  <div key={book.book_id} className="group relative" data-testid={`book-card-${book.book_id}`}>
                    <div 
                      className="aspect-[2/3] rounded-xl overflow-hidden shadow-lg cursor-pointer transform transition-all group-hover:scale-105 group-hover:shadow-xl"
                      onClick={() => handleBookClick(book)}
                    >
                      {book.cover_image ? (
                        <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-amber-200 to-amber-300 flex items-center justify-center">
                          <BookOpen className="w-10 h-10 text-amber-600" />
                        </div>
                      )}
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                        <p className="text-white text-xs font-medium line-clamp-2">{book.title}</p>
                        <p className="text-white/70 text-xs truncate">{book.author}</p>
                        {book.series && (
                          <p className="text-amber-300 text-xs truncate mt-0.5">
                            {book.series.series_name} #{book.series.position || '?'}
                          </p>
                        )}
                      </div>
                      
                      {/* Already read badge */}
                      {alreadyRead && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Read
                        </div>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      {!alreadyRead && (
                        <>
                          {/* Add to List button - opens dialog */}
                          <Button 
                            size="icon" 
                            className="h-7 w-7 bg-white/90 hover:bg-white text-slate-700"
                            onClick={(e) => { e.stopPropagation(); setAddToListItem(book); }}
                            data-testid={`add-to-list-btn-${book.book_id}`}
                          >
                            <List className="w-3.5 h-3.5" />
                          </Button>
                          
                          {/* Mark as read button */}
                          <Button 
                            size="icon" 
                            className="h-7 w-7 bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={(e) => { e.stopPropagation(); openMarkAsRead(book); }}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-full"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Book Details Dialog */}
      <Dialog open={!!selectedBook} onOpenChange={() => { setSelectedBook(null); setBookDetails(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b] pr-8">{selectedBook?.title}</DialogTitle>
            <DialogDescription className="text-[#64748B]">{selectedBook?.author}</DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : bookDetails && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {bookDetails.cover_image && (
                  <img src={bookDetails.cover_image} alt={bookDetails.title} className="w-24 h-36 object-cover rounded-lg shadow" />
                )}
                <div className="flex-1 space-y-2">
                  {/* Series Badge */}
                  {bookDetails.series && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                      <BookOpen className="w-3.5 h-3.5" />
                      {bookDetails.series.series_name} #{bookDetails.series.position || '?'}
                      {bookDetails.series.total_books && (
                        <span className="text-amber-500 text-xs">
                          of {bookDetails.series.total_books}
                        </span>
                      )}
                    </div>
                  )}
                  {bookDetails.page_count > 0 && (
                    <p className="text-sm text-slate-600">{bookDetails.page_count} pages</p>
                  )}
                  {bookDetails.published_date && (
                    <p className="text-sm text-slate-600">Published: {bookDetails.published_date}</p>
                  )}
                  {bookDetails.publisher && (
                    <p className="text-sm text-slate-600">{bookDetails.publisher}</p>
                  )}
                </div>
              </div>
              
              {bookDetails.genres?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {bookDetails.genres.map((genre) => (
                    <span key={genre} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                      {genre}
                    </span>
                  ))}
                </div>
              )}
              
              {bookDetails.description && (
                <div className="max-h-48 overflow-y-auto">
                  <p className="text-sm text-slate-600">{bookDetails.description}</p>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2 pt-2">
                {!isBookRead(bookDetails.book_id) && (
                  <>
                    <Button 
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      onClick={() => { setSelectedBook(null); openMarkAsRead(bookDetails); }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Mark as Read
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => { setSelectedBook(null); setAddToListItem(bookDetails); }}
                    >
                      <List className="w-4 h-4 mr-2" />
                      Add to List
                    </Button>
                  </>
                )}
                <Button 
                  variant="outline"
                  onClick={openSharePanel}
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                {bookDetails.preview_link && (
                  <Button variant="outline" asChild>
                    <a href={bookDetails.preview_link} target="_blank" rel="noopener noreferrer">
                      <Info className="w-4 h-4 mr-2" />
                      Preview
                    </a>
                  </Button>
                )}
              </div>
              
              {/* Share with Friend Panel */}
              {showSharePanel && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-amber-500" />
                    <span className="font-medium text-sm text-[#3c405b]">Share with a friend</span>
                    <button 
                      onClick={() => setShowSharePanel(false)}
                      className="ml-auto text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder={`Check out ${bookDetails?.title}!`}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  />
                  {loadingFriends ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    </div>
                  ) : friends.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">
                      No friends yet. Follow someone and get followed back!
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {friends.slice(0, 6).map((friend) => (
                        <button
                          key={friend.user_id}
                          onClick={() => handleShareWithFriend(friend.user_id, bookDetails)}
                          disabled={sharingWith === friend.user_id}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full hover:border-amber-500 hover:bg-amber-50 transition-colors text-sm disabled:opacity-50"
                        >
                          {sharingWith === friend.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Avatar className="h-5 w-5">
                                {friend.avatar_url ? (
                                  <AvatarImage src={friend.avatar_url} />
                                ) : (
                                  <AvatarFallback className="bg-amber-100 text-amber-700 text-xs">
                                    {friend.name?.charAt(0)}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <span className="text-[#3c405b]">{friend.name?.split(' ')[0]}</span>
                              <Send className="w-3 h-3 text-amber-500" />
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Mark as Read Dialog - Grade Selection */}
      <Dialog open={!!markingBook && !showRanking} onOpenChange={() => resetMarkDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">Mark as Read</DialogTitle>
            <DialogDescription>How would you rate &ldquo;{markingBook?.title}&rdquo;?</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Grade Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Your Rating</Label>
              <RadioGroup value={grade} onValueChange={setGrade} className="flex gap-3">
                <Label className={`flex-1 flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${grade === 'liked' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-green-300'}`}>
                  <RadioGroupItem value="liked" className="sr-only" />
                  <ThumbsUp className={`w-8 h-8 mb-2 ${grade === 'liked' ? 'text-green-500' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${grade === 'liked' ? 'text-green-700' : 'text-slate-600'}`}>Liked</span>
                </Label>
                <Label className={`flex-1 flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${grade === 'alright' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`}>
                  <RadioGroupItem value="alright" className="sr-only" />
                  <Meh className={`w-8 h-8 mb-2 ${grade === 'alright' ? 'text-amber-500' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${grade === 'alright' ? 'text-amber-700' : 'text-slate-600'}`}>Alright</span>
                </Label>
                <Label className={`flex-1 flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${grade === 'disliked' ? 'border-slate-500 bg-slate-50' : 'border-slate-200 hover:border-slate-400'}`}>
                  <RadioGroupItem value="disliked" className="sr-only" />
                  <ThumbsDown className={`w-8 h-8 mb-2 ${grade === 'disliked' ? 'text-slate-500' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${grade === 'disliked' ? 'text-slate-700' : 'text-slate-600'}`}>Disliked</span>
                </Label>
              </RadioGroup>
            </div>
            
            {/* First Read Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm">First time reading?</Label>
              <Button
                type="button"
                variant={isFirstRead ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsFirstRead(!isFirstRead)}
                className={isFirstRead ? 'bg-amber-500 hover:bg-amber-600' : ''}
              >
                {isFirstRead ? 'Yes' : 'No, reread'}
              </Button>
            </div>
            
            {/* Read Date */}
            <div>
              <Label className="text-sm font-medium mb-2 block">When did you finish it?</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {readDate ? format(readDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={readDate} onSelect={setReadDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Primary Genre */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Primary Genre (for stats)</Label>
              <div className="flex flex-wrap gap-2">
                {(markingBook?.genres || []).map((g) => (
                  <Button
                    key={g}
                    type="button"
                    variant={primaryGenre === g && !customGenre ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setPrimaryGenre(g); setCustomGenre(''); }}
                    className={primaryGenre === g && !customGenre ? 'bg-amber-500 hover:bg-amber-600' : ''}
                  >
                    {g}
                  </Button>
                ))}
                {/* Custom Genre Option */}
                <Button
                  type="button"
                  variant={primaryGenre === 'custom' || customGenre ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPrimaryGenre('custom')}
                  className={primaryGenre === 'custom' || customGenre ? 'bg-purple-500 hover:bg-purple-600' : ''}
                  data-testid="genre-select-custom"
                >
                  + Custom
                </Button>
              </div>
              {/* Custom Genre Input */}
              {(primaryGenre === 'custom' || customGenre) && (
                <div className="mt-2">
                  <Input
                    type="text"
                    placeholder="Enter your custom genre..."
                    value={customGenre}
                    onChange={(e) => setCustomGenre(e.target.value)}
                    className="text-sm"
                    data-testid="custom-genre-input"
                  />
                </div>
              )}
            </div>
            
            {/* Comment */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Your thoughts (optional)</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What did you think of this book?"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => resetMarkDialog()}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleContinueToRanking}>
              Continue to Ranking
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Ranking Comparison Dialog */}
      <Dialog open={showRanking} onOpenChange={() => resetMarkDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b] flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Which do you prefer?
            </DialogTitle>
            <DialogDescription>
              Ranking &ldquo;{markingBook?.title}&rdquo; against your {grade} books
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="grid grid-cols-2 gap-4">
              {/* New Book */}
              <button
                onClick={() => handleComparisonChoice(true)}
                disabled={saving}
                className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-left disabled:opacity-50"
              >
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-100 mb-3">
                  {markingBook?.cover_image ? (
                    <img src={markingBook.cover_image} alt={markingBook.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-slate-300" />
                    </div>
                  )}
                </div>
                <p className="font-medium text-[#3c405b] line-clamp-2 text-sm">{markingBook?.title}</p>
                <p className="text-xs text-[#64748B] mt-1 truncate">{markingBook?.author}</p>
              </button>
              
              {/* Comparison Book */}
              <button
                onClick={() => handleComparisonChoice(false)}
                disabled={saving}
                className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-left disabled:opacity-50"
              >
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-100 mb-3">
                  {comparisonItem?.cover_image ? (
                    <img src={comparisonItem.cover_image} alt={comparisonItem.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-slate-300" />
                    </div>
                  )}
                </div>
                <p className="font-medium text-[#3c405b] line-clamp-2 text-sm">{comparisonItem?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">#{comparisonItem?.rank_in_group}</p>
              </button>
            </div>
            
            {saving && (
              <div className="flex items-center justify-center mt-4">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add to List Dialog */}
      <AddToListDialog
        open={!!addToListItem}
        onClose={() => setAddToListItem(null)}
        item={addToListItem}
        watchlists={bookLists.map(list => ({
          ...list,
          // Map books to series format for the dialog
          series: list.books
        }))}
        onAddToList={(listId, item, position) => handleAddToListFromDialog(listId, item, position)}
        onAddAndRank={(list, item) => handleAddAndRankFromDialog(list, item)}
        onCreateList={() => { setAddToListItem(null); navigate('/books'); }}
        loading={addingToList}
        mediaType="book"
        testId="book-add-to-list-dialog"
      />
      
      {/* Add & Rank Dialog */}
      <Dialog open={!!rankingListBook} onOpenChange={() => { setRankingListBook(null); setRankingList(null); setListRankingItems([]); }}>
        <DialogContent className="max-w-md max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b] flex items-center gap-2">
              <ListOrdered className="w-5 h-5 text-amber-500" />
              Rank in List
            </DialogTitle>
            <DialogDescription>
              Drag &ldquo;{rankingListBook?.title}&rdquo; to your preferred position
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2 max-h-[50vh] overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={({ active }) => setActiveRankId(active.id)}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={listRankingItems.map(b => b.book_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {listRankingItems.map((book, index) => (
                    <SortableBookRankItem 
                      key={book.book_id} 
                      book={book} 
                      index={index}
                      isNew={book.isNew}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            
            {createPortal(
              <DragOverlay modifiers={[snapCenterToCursor]}>
                {activeRankId && (
                  <div className="bg-white shadow-xl rounded-lg p-3 flex items-center gap-3 border-2 border-amber-400">
                    <span className="font-bold text-amber-600 w-6">?</span>
                    <div className="w-8 h-12 rounded bg-slate-100 overflow-hidden">
                      {listRankingItems.find(b => b.book_id === activeRankId)?.cover_image && (
                        <img 
                          src={listRankingItems.find(b => b.book_id === activeRankId).cover_image} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      )}
                    </div>
                    <span className="text-sm font-medium truncate">
                      {listRankingItems.find(b => b.book_id === activeRankId)?.title}
                    </span>
                  </div>
                )}
              </DragOverlay>,
              document.body
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRankingListBook(null); setRankingList(null); setListRankingItems([]); }}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600"
              onClick={saveListRanking}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Ranking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sortable book item for Add & Rank
function SortableBookRankItem({ book, index, isNew }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: book.book_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
        isNew 
          ? 'bg-amber-50 border-amber-300' 
          : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
        <GripVertical className="w-5 h-5 text-slate-400" />
      </button>
      <span className={`font-bold w-6 text-center ${isNew ? 'text-amber-600' : 'text-slate-500'}`}>
        {index + 1}
      </span>
      <div className="w-8 h-12 rounded overflow-hidden bg-slate-100 flex-shrink-0">
        {book.cover_image ? (
          <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm truncate ${isNew ? 'text-amber-800' : 'text-[#3c405b]'}`}>
          {book.title}
          {isNew && <span className="ml-2 text-xs bg-amber-200 text-amber-700 px-1.5 py-0.5 rounded">NEW</span>}
        </p>
        <p className="text-xs text-slate-500 truncate">{book.author}</p>
      </div>
    </div>
  );
}
